/**
 * Centralized Date Utilities for Night Audit and Financial Operations
 * Ensures consistent, timezone-safe date parsing, calculation, and formatting across PMS.
 */

/**
 * Safely parse a date string or Date object into a local midnight Date.
 * Prevents UTC timezone shifts (where "YYYY-MM-DD" becomes the previous calendar day in negative UTC offsets).
 *
 * @param {string|Date|number} dateInput - The date input to parse
 * @returns {Date|null} - A Date object set to local midnight (00:00:00.000) or null
 */
export const parseLocalDate = (dateInput) => {
    if (!dateInput) return null;
    if (dateInput instanceof Date) {
        return new Date(dateInput.getFullYear(), dateInput.getMonth(), dateInput.getDate());
    }
    if (typeof dateInput === 'string') {
        const trimmed = dateInput.trim();
        // Matches ISO formats: YYYY-MM-DD, YYYY-MM-DDTHH:mm:ss...
        const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (isoMatch) {
            const [, y, m, d] = isoMatch;
            return new Date(Number(y), Number(m) - 1, Number(d));
        }
        // Matches DD/MM/YYYY or DD-MM-YYYY
        const dmyMatch = trimmed.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
        if (dmyMatch) {
            const [, d, m, y] = dmyMatch;
            return new Date(Number(y), Number(m) - 1, Number(d));
        }
    }
    const fallback = new Date(dateInput);
    if (isNaN(fallback.getTime())) return null;
    return new Date(fallback.getFullYear(), fallback.getMonth(), fallback.getDate());
};

/**
 * Formats a date consistently in British/Indian PMS standard DD/MM/YYYY format.
 *
 * @param {string|Date|number} dateInput - Date to format
 * @returns {string} - Formatted string (e.g., "29/10/2026") or "N/A"
 */
export const formatDateDisplay = (dateInput) => {
    const d = parseLocalDate(dateInput);
    if (!d) return 'N/A';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
};

/**
 * Calculates night audit metrics: last closed date, pending closure date, and days behind.
 * Correctly accounts for completed historical runs vs open hotel business date.
 *
 * @param {string|Date} lastClosedInput - Latest completed audit date from history
 * @param {string|Date} hotelDateInput - Hotel's active business date from hotel settings
 * @param {Date} [todayInput=new Date()] - Reference today date
 * @returns {Object} - Object containing lastClosed, pendingDate, diffDays, and formatted strings
 */
export const calculateAuditDates = (lastClosedInput, hotelDateInput, todayInput = new Date()) => {
    const today = parseLocalDate(todayInput);
    const lastClosed = parseLocalDate(lastClosedInput);
    const hotelDate = parseLocalDate(hotelDateInput);

    let effectiveLastClosed = null;
    let effectivePending = null;
    let diffDays = 0;

    if (lastClosed) {
        effectiveLastClosed = lastClosed;
        // Next pending closure is the day after the last successfully closed date
        effectivePending = new Date(lastClosed.getFullYear(), lastClosed.getMonth(), lastClosed.getDate() + 1);
        const diffTime = today.getTime() - lastClosed.getTime();
        diffDays = Math.max(0, Math.floor(diffTime / (1000 * 60 * 60 * 24)));
    } else if (hotelDate) {
        effectiveLastClosed = hotelDate;
        effectivePending = new Date(hotelDate.getFullYear(), hotelDate.getMonth(), hotelDate.getDate() + 1);
        const diffTime = today.getTime() - hotelDate.getTime();
        diffDays = Math.max(0, Math.floor(diffTime / (1000 * 60 * 60 * 24)));
    }

    return {
        lastClosed: effectiveLastClosed,
        pendingDate: effectivePending,
        diffDays,
        lastClosedFormatted: formatDateDisplay(effectiveLastClosed),
        pendingDateFormatted: formatDateDisplay(effectivePending)
    };
};
